import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Calendar, DollarSign, Clock, UserPlus, Search } from 'lucide-react';
import apiService from '../services/api';

const Dashboard = ({ patients: propPatients }) => {
  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalPatients: 0,
      totalVisits: 0,
      totalRevenue: 0,
      pendingTreatments: 0
    },
    recentPatients: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      let stats = null;
      let recentPatients = [];

      try {
        stats = await apiService.getDashboardStats();
      } catch (statsError) {
        console.warn('Dashboard stats API not available:', statsError.message);
      }

      try {
        const patientsResponse = await apiService.getRecentPatients();
        recentPatients = Array.isArray(patientsResponse) ? patientsResponse :
                        (patientsResponse?.data && Array.isArray(patientsResponse.data)) ? patientsResponse.data : [];
      } catch (patientsError) {
        console.warn('Recent patients API not available:', patientsError.message);
      }

      if (!stats) {
        stats = calculateStatsFromPatients(propPatients || []);
      }

      if (recentPatients.length === 0 && propPatients && Array.isArray(propPatients)) {
        recentPatients = propPatients.slice(-5).reverse();
      }

      setDashboardData({
        stats,
        recentPatients: Array.isArray(recentPatients) ? recentPatients : []
      });

    } catch (err) {
      console.error('Failed to load dashboard data:', err);
      setError(err.message);

      const fallbackStats = calculateStatsFromPatients(propPatients || []);
      const fallbackPatients = propPatients && Array.isArray(propPatients) ?
                              propPatients.slice(-5).reverse() : [];

      setDashboardData({
        stats: fallbackStats,
        recentPatients: fallbackPatients
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateStatsFromPatients = (patients) => {
    if (!Array.isArray(patients)) {
      return {
        totalPatients: 0,
        totalVisits: 0,
        totalRevenue: 0,
        pendingTreatments: 0
      };
    }

    const totalPatients = patients.length;
    const totalVisits = patients.reduce((sum, patient) =>
      sum + (patient.visits ? patient.visits.length : 0), 0
    );
    const totalRevenue = patients.reduce((sum, patient) =>
      sum + (patient.visits ? patient.visits.reduce((visitSum, visit) =>
        visitSum + (visit.fee || 0), 0
      ) : 0), 0
    );
    const pendingTreatments = patients.reduce((sum, patient) =>
      sum + (patient.treatmentRemarks ?
        patient.treatmentRemarks.filter(remark => remark.status === 'pending').length : 0
      ), 0
    );

    return {
      totalPatients,
      totalVisits,
      totalRevenue,
      pendingTreatments
    };
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg p-4 sm:p-6">
          <div className="animate-pulse">
            <div className="h-6 sm:h-8 bg-blue-500 rounded w-3/4 mb-2"></div>
            <div className="h-3 sm:h-4 bg-blue-500 rounded w-1/2"></div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="card">
              <div className="animate-pulse flex items-center">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-300 rounded-lg"></div>
                <div className="ml-3 sm:ml-4 flex-1">
                  <div className="h-3 sm:h-4 bg-gray-300 rounded w-3/4 mb-2"></div>
                  <div className="h-4 sm:h-6 bg-gray-300 rounded w-1/2"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const { stats, recentPatients } = dashboardData;

  return (
    <div className="space-y-4 sm:space-y-6 px-4 sm:px-0">
      {/* Statistics Cards - Responsive Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <div className="card bg-gradient-to-br from-blue-50 to-blue-100">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-blue-600 text-white rounded-lg">
              <Users className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Patients</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalPatients || 0}</p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-green-50 to-green-100">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-green-600 text-white rounded-lg">
              <Calendar className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Visits</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.totalVisits || 0}</p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-yellow-50 to-yellow-100">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-yellow-600 text-white rounded-lg">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Total Revenue</p>
              <p className="text-lg sm:text-2xl font-bold text-gray-900">
                {(stats.totalRevenue || 0).toFixed(2)} MMK
              </p>
            </div>
          </div>
        </div>

        <div className="card bg-gradient-to-br from-red-50 to-red-100">
          <div className="flex items-center">
            <div className="p-2 sm:p-3 bg-red-600 text-white rounded-lg">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6" />
            </div>
            <div className="ml-3 sm:ml-4">
              <p className="text-xs sm:text-sm font-medium text-gray-600">Pending Treatments</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.pendingTreatments || 0}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions & Recent Patients - Stack on Mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        <div className="card">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-3">
            <Link to="/register" className="flex items-center p-3 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors">
              <UserPlus className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mr-3" />
              <span className="font-medium text-gray-900 text-sm sm:text-base">Register New Patient</span>
            </Link>
            <Link to="/search" className="flex items-center p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-colors">
              <Search className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mr-3" />
              <span className="font-medium text-gray-900 text-sm sm:text-base">Find Existing Patient</span>
            </Link>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Recent Patients</h2>
          {Array.isArray(recentPatients) && recentPatients.length > 0 ? (
            <div className="space-y-3">
              {recentPatients.map(patient => (
                <Link
                  key={patient.id}
                  to={`/patient/${patient.id}`}
                  className="flex items-center p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    {(patient.full_name || patient.fullName || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div className="ml-3 min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-sm sm:text-base truncate">
                      {patient.full_name || patient.fullName || 'Unknown Patient'}
                    </p>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">
                      {patient.phone || 'No phone number'}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 mb-2 text-sm sm:text-base">No patients registered yet</p>
              <Link to="/register" className="text-blue-600 hover:text-blue-800 text-sm">
                Register your first patient →
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* System Status */}
      <div className="card">
        <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">System Status</h2>
        <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            <span className="text-sm text-gray-600">Online</span>
          </div>
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-2 ${error ? 'bg-yellow-500' : 'bg-green-500'}`}></div>
            <span className="text-sm text-gray-600">
              {error ? 'API Offline (Using Demo Mode)' : 'API Connected'}
            </span>
          </div>
          {error && (
            <button
              onClick={loadDashboardData}
              className="text-blue-600 hover:text-blue-800 text-sm underline"
            >
              Retry Connection
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;